import { useRef } from "react";
import { ChevronDown } from "lucide-react";
import SidebarItemRow from "./SidebarItemRow";
import SidebarGroupFlyout from "./SidebarGroupFlyout";

/**
 * A single `type:"group"` in the sidebar tree.
 *
 * Expanded rail:
 *   - Clickable header (chevron rotates on open/close).
 *   - Inline children (leaves + subgroups) rendered below.
 *
 * Collapsed rail:
 *   - Icon-only header; on hover, a floating flyout (`SidebarGroupFlyout`)
 *     shows the full child tree.
 *
 * "Roadmap-only" groups (every child disabled) get a dimmed icon + a
 * "Roadmap" chip so the user can distinguish real vs unbuilt sections.
 */
export default function SidebarGroup({
  group,
  collapsed,
  isOpen,
  hoverGroup,
  location,
  isPinned,
  togglePinned,
  toggleGroup,
  onHover,
  onLeave,
  onContextMenu,
}) {
  const GroupIcon = group.icon;
  const triggerRef = useRef(null);
  const groupActive = (group.children || []).some((c) => {
    if (c.type === "subgroup") return (c.children || []).some((gc) => location.pathname === gc.to || location.pathname.startsWith(gc.to + "/"));
    return c.to && (location.pathname === c.to || location.pathname.startsWith(c.to + "/"));
  });
  const showChildren = !collapsed && isOpen;
  const showFlyout = collapsed && hoverGroup === group.key;
  // Dim the group icon when every child is disabled (roadmap-only groups).
  const allChildrenDisabled = (group.children || []).every((c) => {
    if (c.type === "subgroup") return (c.children || []).every((gc) => gc.disabled);
    return c.disabled;
  });
  const groupIconClass = allChildrenDisabled ? "text-slate-600" : (group.color || "text-slate-300");
  const groupLabelClass = allChildrenDisabled ? "text-slate-500" : "";

  return (
    <div
      className="relative"
      onMouseEnter={onHover}
      onMouseLeave={onLeave}
      data-testid={`sidebar-group-${group.key}`}
      data-roadmap-only={allChildrenDisabled ? "true" : undefined}
    >
      <button
        ref={triggerRef}
        type="button"
        onClick={() => !collapsed && toggleGroup(group.key, isOpen)}
        aria-expanded={showChildren}
        data-testid={`sidebar-group-${group.key}-toggle`}
        className={`w-full flex items-center ${collapsed ? "justify-center px-2" : "px-3"} py-2 rounded-md text-sm transition-colors ${
          groupActive
            ? "text-white bg-white/[0.04]"
            : "text-slate-200 hover:bg-white/[0.04] hover:text-white"
        }`}
        title={collapsed ? group.label : undefined}
      >
        <GroupIcon className={`w-4 h-4 shrink-0 ${groupIconClass}`} />
        {!collapsed && (
          <>
            <span className={`ml-2.5 truncate font-medium ${groupLabelClass}`}>{group.label}</span>
            {allChildrenDisabled && (
              <span className="ml-2 text-[8.5px] font-mono uppercase tracking-widest px-1 py-0.5 rounded bg-white/[0.03] text-slate-500 border border-white/[0.06]">
                Roadmap
              </span>
            )}
            <ChevronDown className={`w-3.5 h-3.5 ml-auto text-slate-500 transition-transform ${isOpen ? "" : "-rotate-90"}`} />
          </>
        )}
      </button>

      {/* Inline children (expanded rail) */}
      {showChildren && (
        <div className="mt-0.5 space-y-0.5" data-testid={`sidebar-group-${group.key}-children`}>
          {group.children.map((c) => {
            if (c.type === "subgroup") {
              return (
                <div key={c.key} className="mt-1" data-testid={`sidebar-subgroup-${c.key}`}>
                  <div className="pl-8 pr-3 pt-1 text-[10px] uppercase tracking-wider text-slate-500 flex items-center gap-1.5">
                    <c.icon className={`w-3 h-3 ${c.color || "text-slate-500"}`} />
                    {c.label}
                  </div>
                  {c.children.map((gc) => (
                    <SidebarItemRow
                      key={gc.to}
                      item={gc}
                      collapsed={false}
                      isPinnedItem={isPinned(gc.to)}
                      showStar
                      onTogglePin={() => togglePinned(gc.to)}
                      onContextMenu={(e) => onContextMenu(e, gc.to)}
                      testIdPrefix={`sidebar-nav-${group.key}-sub-${c.key}`}
                    />
                  ))}
                </div>
              );
            }
            return (
              <SidebarItemRow
                key={c.to}
                item={c}
                collapsed={false}
                isPinnedItem={isPinned(c.to)}
                showStar
                onTogglePin={() => togglePinned(c.to)}
                onContextMenu={(e) => onContextMenu(e, c.to)}
                testIdPrefix={`sidebar-nav-${group.key}-sub`}
              />
            );
          })}
        </div>
      )}

      {/* Hover flyout (collapsed rail) — rendered into a portal to escape the sidebar's overflow clip */}
      {showFlyout && (
        <div onMouseEnter={onHover} onMouseLeave={onLeave}>
          <SidebarGroupFlyout
            group={group}
            triggerRef={triggerRef}
            onLinkClick={onLeave}
            onContextMenu={onContextMenu}
            isPinned={isPinned}
          />
        </div>
      )}
    </div>
  );
}
